import { useMemo, useState } from "react";
import { Alert, Badge, Empty, Modal, Working } from "../ui";
import { type Catalog, recordCount } from "./engine";
import { productCode } from "../productCode";
import { productName } from "../productName";

/**
 * Counting the table at a show, from the iPad (quotation C6).
 *
 * Somebody walks the table and counts. It has to work with no signal at all, so the whole
 * count is kept on the iPad and sent as one thing when there is one, and the screen is
 * built for a thumb: the expected number is right there, and the usual answer is "that is
 * what I see", which is one tap.
 *
 * Sealed stock is put right to what was counted. A graded card is only marked seen or not
 * seen: a card worth hundreds is not written off by a tick box at a fair, so a missing one
 * goes to the desk to look into.
 */
export function PosCount({
  catalog,
  counter,
  locationId,
  event,
  onClose,
  onDone,
}: {
  readonly catalog: Catalog;
  readonly counter: { id: string; name: string };
  readonly locationId: string | null;
  readonly event: string | null;
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.ReactElement {
  const [counted, setCounted] = useState<Record<string, number>>({});
  const [seen, setSeen] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"products" | "slabs">("products");
  const [busy, setBusy] = useState(false);

  const term = search.trim().toLowerCase();
  const products = useMemo(
    () =>
      catalog.products.filter((product) =>
        term ? `${productCode(product)} ${productName(product)} ${product.setName ?? ""}`.toLowerCase().includes(term) : true,
      ),
    [catalog.products, term],
  );
  const slabs = useMemo(
    () =>
      catalog.slabs.filter((slab) =>
        term ? `${slab.certNo ?? ""} ${slab.cardName ?? ""}`.toLowerCase().includes(term) : true,
      ),
    [catalog.slabs, term],
  );

  const done = Object.keys(counted).length + Object.keys(seen).length;
  const different = catalog.products.filter(
    (product) => counted[product.id] !== undefined && counted[product.id] !== product.onHand,
  ).length;
  const missing = catalog.slabs.filter((slab) => seen[slab.lotId] === false).length;

  async function save(): Promise<void> {
    setBusy(true);
    try {
      const lines = [
        ...Object.entries(counted).map(([productId, quantity]) => ({ productId, countedQuantity: quantity })),
        ...Object.entries(seen).map(([slabLotId, wasSeen]) => ({ slabLotId, countedQuantity: wasSeen ? 1 : 0 })),
      ];
      await recordCount({ counter, locationId, note: event, lines });
      onDone(
        `Counted ${lines.length} ${lines.length === 1 ? "line" : "lines"}` +
          (different > 0 || missing > 0
            ? `. ${different} different${missing > 0 ? `, ${missing} graded cards not seen` : ""}.`
            : ", all right."),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      size="lg"
      title="Count inventory"
      onClose={onClose}
      footer={
        <>
          <span className="text-[0.813rem] text-[#8c9097] dark:text-white/50 me-auto">
            {done} counted{different > 0 ? `, ${different} different` : ""}
            {missing > 0 ? `, ${missing} not seen` : ""}
          </span>
          <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full !mb-0"
            disabled={busy || done === 0}
            onClick={() => void save()}
          >
            {busy ? <Working label="Saving" /> : `Save the count (${done})`}
          </button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1">
          <button
            type="button"
            className={`ti-btn ${tab === "products" ? "ti-btn-primary" : "ti-btn-light"} !mb-0`}
            onClick={() => setTab("products")}
          >
            Products ({catalog.products.length})
          </button>
          <button
            type="button"
            className={`ti-btn ${tab === "slabs" ? "ti-btn-primary" : "ti-btn-light"} !mb-0`}
            onClick={() => setTab("slabs")}
          >
            Graded cards ({catalog.slabs.length})
          </button>
        </div>
        <input
          id="count-search"
          type="search"
          className="form-control flex-1 min-w-[10rem]"
          placeholder="Search by product name, code or certificate number"
          value={search}
          onChange={(changed) => setSearch(changed.target.value)}
        />
      </div>

      <div className="max-h-[26rem] overflow-y-auto">
        {tab === "products" ? (
          products.length === 0 ? (
            <Empty icon="ri-box-3-line">Nothing matches that.</Empty>
          ) : (
            <ul className="list-none m-0 p-0 divide-y divide-defaultborder dark:divide-defaultborder/10">
              {products.map((product) => {
                const value = counted[product.id];
                const off = value !== undefined && value !== product.onHand;
                return (
                  <li key={product.id} className="py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold mb-0 truncate">{productName(product)}</p>
                      <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
                        {productCode(product)} · Quantity on hand {product.onHand}
                        {off ? ` · ${value > product.onHand ? "+" : ""}${value - product.onHand}` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      className={`ti-btn ${value === product.onHand ? "ti-btn-success" : "ti-btn-light"} !mb-0 !py-1 !px-2 !text-[0.75rem]`}
                      onClick={() => setCounted({ ...counted, [product.id]: product.onHand })}
                    >
                      As it says
                    </button>
                    <input
                      id={`count-${product.id}`}
                      type="text"
                      inputMode="numeric"
                      aria-label={`Counted ${productName(product)}`}
                      className={`form-control field-tiny font-semibold ${off ? "!border-warning" : ""}`}
                      placeholder="-"
                      value={value === undefined ? "" : String(value)}
                      onChange={(changed) => {
                        const digits = changed.target.value.replace(/\D/g, "");
                        const next = { ...counted };
                        if (digits === "") delete next[product.id];
                        else next[product.id] = Number(digits);
                        setCounted(next);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          )
        ) : slabs.length === 0 ? (
          <Empty icon="ri-award-line">No graded cards on this iPad's list.</Empty>
        ) : (
          <ul className="list-none m-0 p-0 divide-y divide-defaultborder dark:divide-defaultborder/10">
            {slabs.map((slab) => {
              const state = seen[slab.lotId];
              return (
                <li key={slab.lotId} className="py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold mb-0 truncate">{slab.cardName ?? "A graded card"}</p>
                    <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
                      {[slab.grader, slab.grade, slab.certNo].filter(Boolean).join(" ")}
                    </p>
                  </div>
                  {state === false ? <Badge tone="danger">Not seen</Badge> : null}
                  <button
                    type="button"
                    className={`ti-btn ${state === true ? "ti-btn-success" : "ti-btn-light"} !mb-0`}
                    onClick={() => setSeen({ ...seen, [slab.lotId]: true })}
                  >
                    <i className="ri-check-line" /> Here
                  </button>
                  <button
                    type="button"
                    className={`ti-btn ${state === false ? "ti-btn-danger" : "ti-btn-light"} !mb-0`}
                    onClick={() => setSeen({ ...seen, [slab.lotId]: false })}
                  >
                    Not here
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {missing > 0 ? (
        <Alert tone="warning">
          {missing} graded {missing === 1 ? "card is" : "cards are"} not on the table. Nothing is written off here: the
          management dashboard is notified to look for {missing === 1 ? "it" : "them"}.
        </Alert>
      ) : null}

      <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-3 mb-0">
        Only the lines you enter are counted; the others are left unchanged. Without a connection the count is saved on
        this iPad and uploads automatically when the connection returns.
      </p>
    </Modal>
  );
}
